import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, X, Send, ImagePlus, Loader2, CheckCircle2, AlertTriangle, Zap } from "lucide-react";

const ACTION_LABEL = {
  send_sms: "Send personalized text",
  update_status: "Change status",
  add_note: "Add note to file",
  create_card: "Add to Work Queue",
};

export default function AdminDucky() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "ducky", text: "Hey! I'm Ducky 🦆 — tell me what to do. Try: \"Text everyone in Disputes Submitted their next step\" or \"Move John Smith to Funding Ready\"." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [image, setImage] = useState(null); // {image_id, url}
  const [uploading, setUploading] = useState(false);
  const sessionId = useRef(`ducky_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [msgs, preview, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setPreview(null);
    setMsgs((m) => [...m, { role: "admin", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ducky/chat", { session_id: sessionId.current, message: text, image_id: image?.image_id || null });
      setMsgs((m) => [...m, { role: "ducky", text: data.reply, evidence: data.evidence, evidence_total: data.evidence_total }]);
      if (data.preview && data.preview.count > 0) setPreview(data.preview);
      else if (data.preview && data.preview.count === 0) setMsgs((m) => [...m, { role: "ducky", text: "I couldn't find anyone matching that. Want to try different wording?" }]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Ducky had trouble");
    } finally { setBusy(false); }
  };

  const doIt = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ducky/execute", {
        action: preview,
        image_id: preview.use_image ? image?.image_id : null,
        public_base: process.env.REACT_APP_BACKEND_URL,
      });
      const ok = data.ok || 0, failed = data.failed || 0;
      setMsgs((m) => [...m, { role: "ducky", text: `✅ Done — ${ok} succeeded${failed ? `, ${failed} skipped` : ""}.` + (failed ? ` (${(data.details || []).filter(d => !d.ok).slice(0, 3).map(d => `${d.name}: ${d.why}`).join("; ")})` : "") }]);
      toast.success(`Ducky finished: ${ok} done${failed ? `, ${failed} skipped` : ""}`);
      setPreview(null);
      setImage(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Execution failed");
    } finally { setBusy(false); }
  };

  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/ducky/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(data);
      toast.success("Picture attached");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-[90] h-14 w-14 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-slate-950 shadow-[0_12px_32px_-6px_rgba(251,146,60,.7)] grid place-items-center hover:scale-105 transition"
          data-testid="ducky-launcher"
          title="Ask Ducky"
        >
          <Sparkles size={24} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-[90] w-[380px] max-w-[94vw] h-[560px] max-h-[82vh] rounded-3xl bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-testid="ducky-panel">
          <div className="flex items-center gap-2 px-4 py-3 bg-slate-900 text-white">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center text-slate-950"><Sparkles size={16} /></div>
            <div className="flex-1">
              <p className="font-display font-black leading-none">Ducky</p>
              <p className="text-[11px] text-slate-400">Your admin assistant</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="ducky-close"><X size={18} /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "admin" ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[88%]">
                  <div className={`rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "admin" ? "bg-slate-900 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`} data-testid={`ducky-msg-${m.role}`}>
                    {m.text}
                  </div>
                  {m.evidence?.length > 0 && (
                    <details className="mt-1.5 bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="ducky-evidence">
                      <summary className="cursor-pointer text-[11px] font-bold text-slate-500 px-3 py-1.5 hover:bg-slate-50">
                        📋 Based on {m.evidence_total || m.evidence.length} message{(m.evidence_total || m.evidence.length) === 1 ? "" : "s"} — show
                      </summary>
                      <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                        {m.evidence.map((e, j) => (
                          <div key={j} className="px-3 py-2 text-xs">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-bold text-slate-800 truncate">{e.client_name}</span>
                              <span className="text-slate-400 shrink-0">{e.date}{!e.is_read ? " · unread" : ""}</span>
                            </div>
                            <p className="text-slate-600 mt-0.5">{e.text}</p>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            ))}

            {preview && (
              <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-3" data-testid="ducky-preview">
                <div className="flex items-center gap-2 text-amber-800 font-black text-sm"><Zap size={14} /> {ACTION_LABEL[preview.type] || preview.type}</div>
                <p className="text-xs text-amber-700 mt-1">
                  {preview.count} {preview.count === 1 ? "person" : "people"}
                  {preview.type === "send_sms" && preview.no_phone ? ` · ${preview.no_phone} without a phone (will skip)` : ""}
                  {preview.type === "update_status" ? ` → ${preview.status}` : ""}
                  {preview.use_image ? " · 📎 picture link" : ""}
                </p>
                {preview.type === "send_sms" && (
                  <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto">
                    {preview.sample.map((s, i) => (
                      <div key={i} className="text-xs bg-white rounded-lg border border-amber-200 px-2 py-1.5">
                        <span className="font-bold text-slate-800">{s.name}</span>{!s.has_phone && <span className="text-rose-500"> · no phone</span>}
                        <p className="text-slate-600 mt-0.5">{s.text}</p>
                      </div>
                    ))}
                    {preview.count > preview.sample.length && <p className="text-[11px] text-amber-700">…and {preview.count - preview.sample.length} more</p>}
                  </div>
                )}
                {preview.type !== "send_sms" && (
                  <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                    {preview.sample.map((s, i) => (<div key={i} className="text-xs text-slate-700">• {s.name}{preview.type === "update_status" ? ` (now: ${s.status || "—"})` : ""}</div>))}
                    {preview.note && <p className="text-xs italic text-slate-600 mt-1">Note: "{preview.note}"</p>}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={doIt} disabled={busy} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-black" data-testid="ducky-doit">
                    {busy ? <Loader2 className="animate-spin" size={14} /> : <><CheckCircle2 size={14} className="mr-1" /> Do it</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setPreview(null)} disabled={busy} data-testid="ducky-cancel">Cancel</Button>
                </div>
              </div>
            )}

            {busy && !preview && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl px-3 py-2"><Loader2 className="animate-spin text-amber-500" size={16} /></div></div>}
          </div>

          {/* Composer */}
          <div className="border-t border-slate-100 p-2.5 bg-white">
            {image && (
              <div className="flex items-center gap-2 mb-2 text-xs bg-slate-100 rounded-lg px-2 py-1.5" data-testid="ducky-image-chip">
                <ImagePlus size={13} className="text-slate-500" /> <span className="flex-1 truncate text-slate-600">Picture attached</span>
                <button onClick={() => setImage(null)} className="text-slate-400 hover:text-rose-500"><X size={13} /></button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <input ref={fileRef} type="file" accept="image/*" onChange={pickImage} className="hidden" data-testid="ducky-file-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="h-10 w-10 shrink-0 rounded-xl border border-slate-200 grid place-items-center text-slate-500 hover:bg-slate-50" data-testid="ducky-attach" title="Attach a picture">
                {uploading ? <Loader2 className="animate-spin" size={16} /> : <ImagePlus size={18} />}
              </button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1}
                placeholder="Tell Ducky what to do…"
                className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 max-h-24"
                data-testid="ducky-input"
              />
              <button onClick={send} disabled={busy || !input.trim()} className="h-10 w-10 shrink-0 rounded-xl bg-amber-400 text-slate-950 grid place-items-center hover:bg-amber-300 disabled:opacity-40" data-testid="ducky-send">
                <Send size={17} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
